import { AdminPageHeader, AdminPageShell } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import {
  getIndexPaginationMeta,
  IndexDataTable,
  IndexTablePagination,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Typography } from '@/components/ui/typography'
import { Can } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import {
  buildCreateDiscountPayload,
  createDiscountSchema,
  defaultDiscountFormValues,
  normalizeCodeInput,
} from '@/lib/services/discounts/discounts.schema'
import {
  discountQueries,
  useCreateDiscountMutation,
  useDiscount,
  useDiscounts,
  useGenerateDiscountCodeMutation,
} from '@/lib/services/discounts/discounts.query'
import { formatCurrency, getErrorMessage } from '@/lib/utils'
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router'
import { Tag } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  CopyDiscountCodeButton,
  DiscountForm,
  EditDiscountForm,
  getValidationErrors,
} from './-DiscountForm'
import {
  discountAvailabilityDisplay,
  discountCountText,
  discountValueLabel,
  usedAtLabel,
} from './-discount-display'

function AvailabilityCell({ discount }) {
  const availability = discountAvailabilityDisplay(discount)

  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <Badge variant={availability.variant} size="status">
        {availability.label}
      </Badge>
      <Typography variant="caption" className="max-w-full truncate">
        {availability.detail}
      </Typography>
    </div>
  )
}

function normalizeDiscountIdParam(value) {
  const id = Number(
    String(value ?? '')
      .trim()
      .replace(/^["']+|["']+$/g, '')
      .trim()
  )

  return Number.isInteger(id) && id > 0 ? id : undefined
}

const DEFAULT_DISCOUNT_PAGE_SIZE = 20
const MAX_DISCOUNT_PAGE_SIZE = 100

function normalizePositiveIntegerParam(value, fallback) {
  const normalizedValue = Number(value)

  return Number.isInteger(normalizedValue) && normalizedValue > 0 ? normalizedValue : fallback
}

function normalizeDiscountPageSizeParam(value) {
  return Math.min(
    normalizePositiveIntegerParam(value, DEFAULT_DISCOUNT_PAGE_SIZE),
    MAX_DISCOUNT_PAGE_SIZE
  )
}

function normalizeDiscountRouteSearch(search = {}) {
  return {
    discountId: normalizeDiscountIdParam(search.discountId),
    code: normalizeCodeInput(search.code) || undefined,
    search: String(search.search ?? '').trim() || undefined,
    sort: ['organization_asc', 'organization_desc'].includes(search.sort) ? search.sort : undefined,
    page: normalizePositiveIntegerParam(search.page, 1),
    pageSize: normalizeDiscountPageSizeParam(search.pageSize),
  }
}

export const Route = createFileRoute('/_authenticated/$orgId/marketing/discounts/')({
  component: RouteComponent,
  validateSearch: normalizeDiscountRouteSearch,
  loader: ({ context, location, params }) => {
    if (!context.ability.can('list', 'discounts')) throw notFound()

    const routeSearch = normalizeDiscountRouteSearch(location.search)
    const isGlobalOrg = Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)
    const canViewDiscounts = !isGlobalOrg && context.ability.can('view', 'discounts')

    void context.queryClient.prefetchQuery(
      discountQueries.index({
        organizationId: params.orgId,
        search: (canViewDiscounts && routeSearch.code) || routeSearch.search,
        sort: routeSearch.sort,
        page: routeSearch.page,
        pageSize: routeSearch.pageSize,
      })
    )

    if (routeSearch.discountId && canViewDiscounts) {
      void context.queryClient.prefetchQuery(
        discountQueries.show({
          organizationId: params.orgId,
          discountId: routeSearch.discountId,
        })
      )
    }

    return {
      canViewDiscounts,
      isGlobalOrg,
      isReadOnly: isGlobalOrg || !context.ability.can('edit', 'discounts'),
    }
  },
})

export function RouteComponent() {
  const routeSearch = Route.useSearch()
  const { canViewDiscounts, isGlobalOrg } = Route.useLoaderData()
  const navigate = useNavigate()
  const deepLinkedDiscountId = canViewDiscounts
    ? normalizeDiscountIdParam(routeSearch.discountId)
    : undefined
  const deepLinkedCode = canViewDiscounts ? normalizeCodeInput(routeSearch.code) : undefined
  const tableSearch = routeSearch.search ?? ''
  const listSearch = deepLinkedCode || tableSearch
  const currentPage = routeSearch.page ?? 1
  const pageSize = routeSearch.pageSize ?? DEFAULT_DISCOUNT_PAGE_SIZE
  const {
    data: discountsResponse,
    isFetching,
    isError,
    isPending,
    refetch,
  } = useDiscounts({
    search: listSearch,
    sort: routeSearch.sort,
    page: currentPage,
    pageSize,
  })
  const discounts = discountsResponse?.data ?? []
  const paginationMeta = getIndexPaginationMeta(discountsResponse, currentPage, pageSize)
  const deepLinkedDiscountQuery = useDiscount({
    discountId: deepLinkedDiscountId,
    enabled: Boolean(deepLinkedDiscountId),
  })
  const deepLinkKey = deepLinkedDiscountId ? `id:${deepLinkedDiscountId}` : deepLinkedCode
  const findDeepLinkedDiscount = () =>
    deepLinkedDiscountId
      ? (deepLinkedDiscountQuery.data ??
        discounts.find((discount) => Number(discount.id) === deepLinkedDiscountId))
      : discounts.find((discount) => normalizeCodeInput(discount.code) === deepLinkedCode)
  const createDiscount = useCreateDiscountMutation()
  const generateCode = useGenerateDiscountCodeMutation()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [selectedDiscount, setSelectedDiscount] = useState(null)
  const [dismissedDeepLinkKey, setDismissedDeepLinkKey] = useState('')
  const createForm = useAppForm({
    defaultValues: defaultDiscountFormValues(),
    validators: { onSubmit: createDiscountSchema },
    onSubmit: async ({ value, formApi }) => {
      try {
        await createDiscount.mutateAsync(buildCreateDiscountPayload(value))
        toast.success('Discount code created')
        setIsCreateOpen(false)
        formApi.reset(defaultDiscountFormValues())
      } catch (error) {
        const validationErrors = getValidationErrors(error)

        if (Object.keys(validationErrors).length > 0) {
          formApi.setErrorMap({ onSubmit: { fields: validationErrors } })
        } else {
          toast.error('Discount code could not be created', {
            description: getErrorMessage(error) || 'Please try again.',
          })
        }
      }
    },
  })
  const hasActiveSearch = Boolean(listSearch)
  const deepLinkedDiscount =
    deepLinkKey && dismissedDeepLinkKey !== deepLinkKey ? findDeepLinkedDiscount() : null
  const openDiscount = canViewDiscounts ? (deepLinkedDiscount ?? selectedDiscount) : null

  const navigateToListState = (updates) => {
    void navigate({
      search: {
        ...routeSearch,
        ...updates,
      },
      replace: true,
      resetScroll: false,
    })
  }

  const handleSearchCommit = (nextSearch) => {
    navigateToListState({
      code: undefined,
      discountId: undefined,
      search: String(nextSearch ?? '').trim() || undefined,
      page: 1,
    })
  }

  const handlePageChange = (page) => {
    navigateToListState({ page })
  }

  const handlePageSizeChange = (nextPageSize) => {
    navigateToListState({
      page: 1,
      pageSize: normalizeDiscountPageSizeParam(nextPageSize),
    })
  }

  const columns = useMemo(
    () => [
      {
        id: 'code',
        header: 'Code',
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-2 font-medium">
            <span className="min-w-0 truncate">{row.original.code}</span>
            <CopyDiscountCodeButton code={row.original.code} className="-my-1 -mr-1" />
          </div>
        ),
        meta: { label: 'Code', kind: 'primary', preferredWidth: '12rem' },
      },
      ...(isGlobalOrg
        ? [
            {
              id: 'organization',
              header: 'Organization',
              accessorFn: (discount) => discount.organization?.name ?? '',
              cell: ({ row }) => (
                <span className="block truncate">
                  {row.original.organization?.name || 'Unknown organization'}
                </span>
              ),
              meta: {
                label: 'Organization',
                preferredWidth: '14rem',
                sortable: {
                  asc: 'organization_asc',
                  desc: 'organization_desc',
                  defaultSort: 'organization_asc',
                },
              },
            },
          ]
        : []),
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        meta: { label: 'Name', preferredWidth: '14rem' },
      },
      {
        id: 'discount',
        header: 'Discount',
        enableSorting: false,
        accessorFn: discountValueLabel,
        meta: { label: 'Discount', preferredWidth: '12rem' },
      },
      {
        id: 'uses',
        header: 'Used',
        enableSorting: false,
        accessorFn: (discount) => discount.usage_count ?? 0,
        meta: { label: 'Used', kind: 'number', preferredWidth: '7rem' },
      },
      {
        id: 'last_used',
        header: 'Last used',
        enableSorting: false,
        accessorFn: (discount) =>
          usedAtLabel(discount.last_used_at, discount.organization?.timezone),
        meta: { label: 'Last used', preferredWidth: '10rem' },
      },
      {
        id: 'sales_after_discount',
        header: 'Sales after discount',
        enableSorting: false,
        accessorFn: (discount) => Number(discount.net_order_value ?? 0),
        cell: ({ getValue }) => formatCurrency(getValue()),
        meta: { label: 'Sales after discount', kind: 'money', preferredWidth: '12rem' },
      },
      {
        id: 'availability',
        header: 'Availability',
        enableSorting: false,
        accessorFn: (discount) => discountAvailabilityDisplay(discount).label,
        cell: ({ row }) => <AvailabilityCell discount={row.original} />,
        meta: { label: 'Availability', preferredWidth: '10rem' },
      },
    ],
    [isGlobalOrg]
  )

  const openDetails = (discount) => {
    setDismissedDeepLinkKey(deepLinkKey || '')
    setSelectedDiscount(discount)
  }

  const closeDetails = () => {
    if (deepLinkedDiscount && deepLinkKey) {
      setDismissedDeepLinkKey(deepLinkKey)
      navigateToListState({
        code: undefined,
        discountId: undefined,
      })
    }

    setSelectedDiscount(null)
  }

  const closeCreate = () => {
    setIsCreateOpen(false)
    createForm.reset(defaultDiscountFormValues())
  }

  const handleGenerateCode = async () => {
    try {
      const response = await generateCode.mutateAsync({
        name: createForm.getFieldValue('name'),
      })
      createForm.setFieldValue('code', response.code)
    } catch (error) {
      const validationErrors = getValidationErrors(error)

      if (Object.keys(validationErrors).length > 0) {
        createForm.setErrorMap({ onSubmit: { fields: validationErrors } })
      } else {
        toast.error('Discount code could not be generated', {
          description: getErrorMessage(error) || 'Please try again.',
        })
      }
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader
        icon={Tag}
        title="Discount Codes"
        description={
          isGlobalOrg
            ? 'Review storefront discount codes across organizations.'
            : 'Create and manage storefront discount codes.'
        }
        actions={
          isGlobalOrg ? null : (
            <Can do="create" on="discounts">
              <CreateActionButton onClick={() => setIsCreateOpen(true)}>
                New Discount Code
              </CreateActionButton>
            </Can>
          )
        }
      />

      {isGlobalOrg ? (
        <OrganizationContextNotice>
          Discount codes are organization-specific. This read-only view includes codes from all
          organizations.
        </OrganizationContextNotice>
      ) : null}

      <IndexDataTable
        data={discounts}
        columns={columns}
        sort={routeSearch.sort}
        onSortChange={(sort) => navigateToListState({ sort, page: 1 })}
        isLoading={isPending}
        onRowClick={canViewDiscounts ? openDetails : undefined}
        toolbar={
          <IndexTableToolbar
            primary={
              <IndexTableSearchBox
                value={tableSearch}
                onCommit={handleSearchCommit}
                countText={discountCountText(paginationMeta.total ?? 0, hasActiveSearch)}
                isFetching={isFetching}
                placeholder="Search name or code..."
              />
            }
          />
        }
        emptyState={
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <Tag className="size-8 text-muted-foreground" />
            <Typography variant="supporting">
              {isError
                ? 'Discount codes could not be loaded.'
                : hasActiveSearch
                  ? 'No discount codes match that search.'
                  : 'No discount codes yet.'}
            </Typography>
            {isError ? (
              <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
                Retry
              </Button>
            ) : null}
          </div>
        }
      />

      <IndexTablePagination
        meta={paginationMeta}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        summary={discountCountText(paginationMeta.total ?? 0, hasActiveSearch)}
      />

      {!isGlobalOrg ? (
        <Sheet
          open={isCreateOpen}
          onOpenChange={(open) => (open ? setIsCreateOpen(true) : closeCreate())}
        >
          <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
            <createForm.AppForm>
              <createForm.Form className="flex min-h-0 flex-1 flex-col">
                <SheetHeader className="border-b px-6 py-4 text-left">
                  <SheetTitle>Create discount code</SheetTitle>
                  <SheetDescription>
                    Discount values cannot be edited after creation.
                  </SheetDescription>
                </SheetHeader>
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                  <DiscountForm
                    mode="create"
                    form={createForm}
                    onGenerateCode={handleGenerateCode}
                    isGeneratingCode={generateCode.isPending}
                  />
                </div>
                <SheetFooter className="flex-row justify-end border-t px-6 py-4">
                  <Button type="button" variant="outline" onClick={closeCreate}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createDiscount.isPending}>
                    Create
                  </Button>
                </SheetFooter>
              </createForm.Form>
            </createForm.AppForm>
          </SheetContent>
        </Sheet>
      ) : null}

      <Sheet open={Boolean(openDiscount)} onOpenChange={(open) => !open && closeDetails()}>
        <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
          {openDiscount ? (
            <EditDiscountForm
              key={openDiscount.id}
              discount={openDiscount}
              onClose={closeDetails}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </AdminPageShell>
  )
}
